/**
 * calcIndicators.js
 * ─────────────────────────────────────────────────────────────────────────────
 * All indicator calculation functions for MarketPulze charts.
 *
 * Design principles:
 *   1. Every function is PURE — same input always produces same output
 *   2. No React, no D3, no DOM — only plain JS maths
 *   3. Input format: raw candle array [{t, o, h, l, c, v}, ...]
 *      (t = unix seconds, o/h/l/c = price floats, v = volume)
 *   4. Output format: [{time, value}, ...] — ready for D3 line rendering
 *   5. Each function handles its own edge cases (empty input, insufficient data)
 *      and returns [] rather than throwing, so the chart never crashes
 *
 * Unit testing:
 *   These are the highest-value functions to test — a wrong RSI or Stochastic
 *   value silently produces bad signals for every user. Since they are pure,
 *   Jest tests require zero mocking. Example:
 *     const data = mockCandles(30, 100); // 30 candles at $100
 *     expect(calcRSI(data, 14).at(-1).value).toBe(50); // flat price = 50 RSI
 *
 * Exports (20 functions):
 *   calcSMA, calcEMA, calcBollinger, calcVWAPBands
 *   calcMACD, calcRSI, calcStochastic, calcATR, calcATR14Single
 *   calcOBV, calcCCI, calcWilliamsR, calcMFI, calcROC
 *   calcPSAR, calcADX, calcIchimoku, calcDonchian
 *   calcHeikinAshi, calcRenko, calcAnchoredVWAP, calcVolumeProfile
 *   detectSMACrossover, getMarketStatus
 *   fmtPrice, fmtVol  (display helpers — here because they format indicator values)
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Moving Averages ────────────────────────────────────────────────────────────

/**
 * calcSMA — Simple Moving Average
 * Smooths price by averaging the last N closes.
 * Fast but lags. Use with EMA to spot crossovers (golden cross / death cross).
 */
export function calcSMA(c, p) {
  if (!c || c.length < p) return [];
  const r = [];
  for (let i = p - 1; i < c.length; i++) {
    let s = 0;
    for (let j = i - p + 1; j <= i; j++) s += c[j].c;
    r.push({ time: c[i].t, value: s / p });
  }
  return r;
}

/**
 * calcEMA — Exponential Moving Average
 * Weights recent closes more heavily than older ones.
 * Reacts faster than SMA to price changes. Most day-trader indicators use EMA.
 * k = 2/(period+1) is the standard smoothing factor.
 */
export function calcEMA(c, p) {
  if (!c || c.length < p) return [];
  const k = 2 / (p + 1);
  let e = 0;
  for (let i = 0; i < p; i++) e += c[i].c;
  e /= p;
  const r = [{ time: c[p - 1].t, value: e }];
  for (let i = p; i < c.length; i++) {
    e = c[i].c * k + e * (1 - k);
    r.push({ time: c[i].t, value: e });
  }
  return r;
}

/**
 * calcBollinger — Bollinger Bands (SMA ± N standard deviations)
 * Returns upper, middle (SMA), and lower bands.
 * Wide bands = high volatility. Narrow bands (squeeze) often precede breakouts.
 * Price touching/crossing outer bands does NOT alone signal reversal — use with RSI.
 */
export function calcBollinger(c, p, m) {
  const pr = p || 20, mr = m || 2;
  if (!c || c.length < pr) return { upper: [], middle: [], lower: [] };
  const u = [], mid = [], lo = [];
  for (let i = pr - 1; i < c.length; i++) {
    let s = 0;
    for (let j = i - pr + 1; j <= i; j++) s += c[j].c;
    const mn = s / pr;
    let v = 0;
    for (let j = i - pr + 1; j <= i; j++) v += (c[j].c - mn) ** 2;
    const std = Math.sqrt(v / pr);
    mid.push({ time: c[i].t, value: mn });
    u.push({ time: c[i].t, value: mn + mr * std });
    lo.push({ time: c[i].t, value: mn - mr * std });
  }
  return { upper: u, middle: mid, lower: lo };
}

/**
 * calcVWAPBands — VWAP with ±1σ and ±2σ standard deviation bands
 * Runs cumulatively from first bar (session-reset on 1D via data slicing).
 * ±1σ bands contain ~68% of price action; ±2σ contain ~95%.
 * Institutional traders use these bands to judge whether price is extended.
 */
export function calcVWAPBands(c) {
  if (!c || !c.length) return { vwap: [], upper1: [], lower1: [], upper2: [], lower2: [] };
  let ct = 0, cv = 0, ctp2 = 0;
  const vwapLine = [], u1 = [], l1 = [], u2 = [], l2 = [];
  c.forEach(d => {
    const tp = (d.h + d.l + d.c) / 3;
    ct   += tp * d.v;
    cv   += d.v;
    ctp2 += tp * tp * d.v;
    const vwapVal  = cv > 0 ? ct / cv : tp;
    const variance = cv > 0 ? Math.max(0, ctp2 / cv - vwapVal * vwapVal) : 0;
    const sd       = Math.sqrt(variance);
    vwapLine.push({ time: d.t, value: vwapVal });
    u1.push({ time: d.t, value: vwapVal + sd });
    l1.push({ time: d.t, value: vwapVal - sd });
    u2.push({ time: d.t, value: vwapVal + 2 * sd });
    l2.push({ time: d.t, value: vwapVal - 2 * sd });
  });
  return { vwap: vwapLine, upper1: u1, lower1: l1, upper2: u2, lower2: l2 };
}

// ── Momentum Indicators ────────────────────────────────────────────────────────

/**
 * calcMACD — Moving Average Convergence Divergence
 * Returns macdLine (fast EMA - slow EMA), signalLine (EMA of MACD),
 * and histogram (MACD - signal). Histogram sign flip = crossover signal.
 * Default: fast=12, slow=26, signal=9 (standard settings)
 */
export function calcMACD(c, f, s, sig) {
  const fr = f || 12, sr = s || 26, sr2 = sig || 9;
  const fe = calcEMA(c, fr), se = calcEMA(c, sr);
  if (!fe.length || !se.length) return { macdLine: [], signalLine: [], histogram: [] };
  const off = fr > sr ? 0 : sr - fr, ml = [];
  for (let i = 0; i < se.length; i++) {
    const fi = i + off;
    if (fi < fe.length) ml.push({ time: se[i].time, value: fe[fi].value - se[i].value });
  }
  if (ml.length < sr2) return { macdLine: ml, signalLine: [], histogram: [] };
  const k = 2 / (sr2 + 1);
  let se3 = ml.slice(0, sr2).reduce((a, b) => a + b.value, 0) / sr2;
  const sl = [{ time: ml[sr2 - 1].time, value: se3 }];
  const hi = [{ time: ml[sr2 - 1].time, value: ml[sr2 - 1].value - se3, color: ml[sr2 - 1].value >= se3 ? "rgba(16,185,129,0.7)" : "rgba(239,68,68,0.7)" }];
  for (let i = sr2; i < ml.length; i++) {
    se3 = ml[i].value * k + se3 * (1 - k);
    sl.push({ time: ml[i].time, value: se3 });
    hi.push({ time: ml[i].time, value: ml[i].value - se3, color: ml[i].value >= se3 ? "rgba(16,185,129,0.7)" : "rgba(239,68,68,0.7)" });
  }
  return { macdLine: ml, signalLine: sl, histogram: hi };
}

/**
 * calcRSI — Relative Strength Index (0–100)
 * Uses Wilder's smoothing (not simple average) for the gain/loss EMA.
 * > 70 = overbought, < 30 = oversold.
 * Divergence (price makes new high, RSI doesn't) is a leading reversal signal.
 */
export function calcRSI(c, p) {
  const pr = p || 14;
  if (!c || c.length < pr + 1) return [];
  const cl = c.map(d => d.c);
  let ag = 0, al = 0;
  for (let i = 1; i <= pr; i++) {
    const ch = cl[i] - cl[i - 1];
    if (ch > 0) ag += ch; else al += Math.abs(ch);
  }
  ag /= pr; al /= pr;
  const r = [{ time: c[pr].t, value: al === 0 ? 100 : 100 - 100 / (1 + ag / al) }];
  for (let i = pr + 1; i < c.length; i++) {
    const ch = cl[i] - cl[i - 1];
    ag = (ag * (pr - 1) + (ch > 0 ? ch : 0)) / pr;
    al = (al * (pr - 1) + (ch < 0 ? Math.abs(ch) : 0)) / pr;
    r.push({ time: c[i].t, value: al === 0 ? 100 : 100 - 100 / (1 + ag / al) });
  }
  return r;
}

/**
 * calcStochastic — Stochastic Oscillator (%K and %D)
 * %K = where current close sits within the N-bar high/low range (0–100)
 * %D = SMA of %K — the signal line
 * > 80 = overbought, < 20 = oversold. %K crossing %D = entry/exit signal.
 */
export function calcStochastic(c, k, d) {
  const kp = k || 14, dp = d || 3;
  if (!c || c.length < kp) return { kLine: [], dLine: [] };
  const kr = [];
  for (let i = kp - 1; i < c.length; i++) {
    let lo = Infinity, hi = -Infinity;
    for (let j = i - kp + 1; j <= i; j++) {
      if (c[j].l < lo) lo = c[j].l;
      if (c[j].h > hi) hi = c[j].h;
    }
    kr.push({ time: c[i].t, value: hi === lo ? 50 : ((c[i].c - lo) / (hi - lo)) * 100 });
  }
  if (kr.length < dp) return { kLine: kr, dLine: [] };
  const dl = [];
  for (let i = dp - 1; i < kr.length; i++) {
    let s = 0;
    for (let j = i - dp + 1; j <= i; j++) s += kr[j].value;
    dl.push({ time: kr[i].time, value: s / dp });
  }
  return { kLine: kr, dLine: dl };
}

/**
 * calcATR — Average True Range (Wilder's smoothing)
 * True Range = max(H-L, |H-prevC|, |L-prevC|)
 * ATR = Wilder's EMA of TR over N bars.
 * Used for position sizing (stops at 1–2× ATR) and volatility assessment.
 */
export function calcATR(c, p) {
  const pr = p || 14;
  if (!c || c.length < 2) return [];
  const tr = c.map((d, i) =>
    i === 0
      ? d.h - d.l
      : Math.max(d.h - d.l, Math.abs(d.h - c[i - 1].c), Math.abs(d.l - c[i - 1].c))
  );
  if (tr.length < pr) return [];
  let a = tr.slice(0, pr).reduce((x, y) => x + y, 0) / pr;
  const r = [{ time: c[pr - 1].t, value: a }];
  for (let i = pr; i < c.length; i++) {
    a = (a * (pr - 1) + tr[i]) / pr;
    r.push({ time: c[i].t, value: a });
  }
  return r;
}

/** Returns only the last ATR(14) value — used for Renko brick sizing */
export function calcATR14Single(c) {
  const d = calcATR(c, 14);
  return d.length ? d[d.length - 1].value : null;
}

// ── Volume Indicators ─────────────────────────────────────────────────────────

/**
 * calcOBV — On-Balance Volume
 * Cumulative: +volume on up days, -volume on down days.
 * Rising OBV while price is flat = accumulation (institutions buying quietly).
 * Divergence from price is a warning sign.
 */
export function calcOBV(c) {
  if (!c || !c.length) return [];
  let o = 0;
  const r = [{ time: c[0].t, value: 0 }];
  for (let i = 1; i < c.length; i++) {
    if (c[i].c > c[i - 1].c)      o += c[i].v;
    else if (c[i].c < c[i - 1].c) o -= c[i].v;
    r.push({ time: c[i].t, value: o });
  }
  return r;
}

/**
 * calcMFI — Money Flow Index (volume-weighted RSI)
 * Like RSI but weighs each bar by its volume. > 80 = overbought, < 20 = oversold.
 * More reliable than RSI in high-volume environments.
 */
export function calcMFI(c, p) {
  const pr = p || 14;
  if (!c || c.length < pr + 1) return [];
  const tp = c.map(d => ({ t: (d.h + d.l + d.c) / 3, v: d.v }));
  const r = [];
  for (let i = pr; i < c.length; i++) {
    let po = 0, ne = 0;
    for (let j = i - pr + 1; j <= i; j++) {
      const mf = tp[j].t * tp[j].v;
      if (tp[j].t > tp[j - 1].t) po += mf; else ne += mf;
    }
    r.push({ time: c[i].t, value: ne === 0 ? 100 : 100 - (100 / (1 + po / ne)) });
  }
  return r;
}

// ── Oscillators ────────────────────────────────────────────────────────────────

/**
 * calcCCI — Commodity Channel Index
 * Measures how far price deviates from its statistical mean.
 * > +100 = overbought, < -100 = oversold. Useful for cyclical stocks.
 * 0.015 is Lambert's constant — makes ~70–80% of values fall within ±100.
 */
export function calcCCI(c, p) {
  const pr = p || 20;
  if (!c || c.length < pr) return [];
  const r = [];
  for (let i = pr - 1; i < c.length; i++) {
    const sl = c.slice(i - pr + 1, i + 1);
    const tp = sl.map(d => (d.h + d.l + d.c) / 3);
    const mn = tp.reduce((a, b) => a + b, 0) / pr;
    const md = tp.reduce((a, b) => a + Math.abs(b - mn), 0) / pr;
    r.push({ time: c[i].t, value: md === 0 ? 0 : (tp[pr - 1] - mn) / (0.015 * md) });
  }
  return r;
}

/**
 * calcWilliamsR — Williams %R (-100 to 0)
 * Inverted Stochastic. Near 0 = overbought, near -100 = oversold.
 * Faster than Stochastic, better for detecting momentum shifts in trending markets.
 */
export function calcWilliamsR(c, p) {
  const pr = p || 14;
  if (!c || c.length < pr) return [];
  const r = [];
  for (let i = pr - 1; i < c.length; i++) {
    const sl = c.slice(i - pr + 1, i + 1);
    const hi = Math.max(...sl.map(d => d.h));
    const lo = Math.min(...sl.map(d => d.l));
    r.push({ time: c[i].t, value: hi === lo ? -50 : ((hi - c[i].c) / (hi - lo)) * -100 });
  }
  return r;
}

/**
 * calcROC — Rate of Change
 * Percentage change in close price over N bars. Crosses above/below zero = trend change.
 * Simple but effective momentum indicator. Divergence from price = warning signal.
 */
export function calcROC(c, p) {
  const pr = p || 12;
  if (!c || c.length < pr + 1) return [];
  const r = [];
  for (let i = pr; i < c.length; i++) {
    const pv = c[i - pr].c;
    r.push({ time: c[i].t, value: pv === 0 ? 0 : ((c[i].c - pv) / pv) * 100 });
  }
  return r;
}

/**
 * calcADX — Average Directional Index with +DI/-DI lines
 * ADX > 25 = strong trend (direction doesn't matter for ADX itself).
 * +DI > -DI = bullish, -DI > +DI = bearish.
 * ADX rising while +DI > -DI = strong uptrend. ADX falling = trend weakening.
 */
export function calcADX(c, p) {
  const pr = p || 14;
  if (!c || c.length < pr * 2) return { adx: [], plusDI: [], minusDI: [] };
  const tr = [], pd = [], md = [];
  for (let i = 1; i < c.length; i++) {
    const h = c[i].h, l = c[i].l, pc = c[i - 1].c, ph = c[i - 1].h, pl = c[i - 1].l;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
    const up = h - ph, dn = pl - l;
    pd.push(up > dn && up > 0 ? up : 0);
    md.push(dn > up && dn > 0 ? dn : 0);
  }
  let sT = tr.slice(0, pr).reduce((a, b) => a + b, 0);
  let sP = pd.slice(0, pr).reduce((a, b) => a + b, 0);
  let sM = md.slice(0, pr).reduce((a, b) => a + b, 0);
  const adx = [], pdi = [], ndi = [];
  let av = 0;
  for (let i = pr; i < tr.length; i++) {
    sT = sT - sT / pr + tr[i];
    sP = sP - sP / pr + pd[i];
    sM = sM - sM / pr + md[i];
    const pi = sT === 0 ? 0 : (sP / sT) * 100;
    const ni = sT === 0 ? 0 : (sM / sT) * 100;
    const dx = Math.abs(pi - ni) / ((pi + ni) || 1) * 100;
    av = i === pr ? dx : (av * (pr - 1) + dx) / pr;
    const t = c[i + 1] ? c[i + 1].t : c[i].t;
    adx.push({ time: t, value: av });
    pdi.push({ time: t, value: pi });
    ndi.push({ time: t, value: ni });
  }
  return { adx, plusDI: pdi, minusDI: ndi };
}

// ── Overlay Indicators ────────────────────────────────────────────────────────

/**
 * calcPSAR — Parabolic SAR
 * Dots above price = downtrend; below price = uptrend.
 * Flip from above to below (or vice versa) = potential trend reversal.
 * step (acceleration factor) = 0.02, max = 0.2 are Wilder's original defaults.
 */
export function calcPSAR(c, st, ms) {
  const s = st || 0.02, m = ms || 0.2;
  if (!c || c.length < 2) return [];
  const r = [];
  let bull = true, sar = c[0].l, ep = c[0].h, af = s;
  for (let i = 1; i < c.length; i++) {
    const p = c[i - 1], d = c[i];
    let ns = sar + af * (ep - sar);
    if (bull) {
      ns = Math.min(ns, p.l, i > 1 ? c[i - 2].l : p.l);
      if (d.l < ns) { bull = false; sar = ep; ep = d.l; af = s; }
      else { if (d.h > ep) { ep = d.h; af = Math.min(af + s, m); } sar = ns; }
    } else {
      ns = Math.max(ns, p.h, i > 1 ? c[i - 2].h : p.h);
      if (d.h > ns) { bull = true; sar = ep; ep = d.h; af = s; }
      else { if (d.l < ep) { ep = d.l; af = Math.min(af + s, m); } sar = ns; }
    }
    r.push({ time: d.t, value: sar, bull });
  }
  return r;
}

/**
 * calcIchimoku — Ichimoku Kinko Hyo (5 lines + cloud)
 * Tenkan (9): short-term trend signal
 * Kijun  (26): medium-term trend signal / support-resistance
 * Senkou A (26 ahead): leading cloud boundary
 * Senkou B (52 ahead): leading cloud boundary (stronger support/resistance)
 * Chikou  (26 behind): lagging confirmation
 * Cloud colour: A > B = bullish (green), A < B = bearish (red)
 */
export function calcIchimoku(c, params) {
  const { tenkan = 9, kijun = 26, senkou = 52 } = params || {};
  if (!c || c.length < senkou) return { tenkan: [], kijun: [], senkouA: [], senkouB: [], chikou: [] };
  const mhl = (arr, i, n) => {
    const sl = arr.slice(Math.max(0, i - n + 1), i + 1);
    return (Math.max(...sl.map(d => d.h)) + Math.min(...sl.map(d => d.l))) / 2;
  };
  const tl = [], kl = [], sa = [], sb = [], chi = [];
  for (let i = 0; i < c.length; i++) {
    if (i >= tenkan - 1) tl.push({ time: c[i].t, value: mhl(c, i, tenkan) });
    if (i >= kijun - 1) {
      kl.push({ time: c[i].t, value: mhl(c, i, kijun) });
      const fi = Math.min(i + kijun, c.length - 1);
      sa.push({ time: c[fi].t, value: (mhl(c, i, tenkan) + mhl(c, i, kijun)) / 2 });
    }
    if (i >= senkou - 1) {
      const fi = Math.min(i + kijun, c.length - 1);
      sb.push({ time: c[fi].t, value: mhl(c, i, senkou) });
    }
    chi.push({ time: c[Math.max(0, i - kijun)].t, value: c[i].c });
  }
  return { tenkan: tl, kijun: kl, senkouA: sa, senkouB: sb, chikou: chi };
}

/**
 * calcDonchian — Donchian Channel (highest high / lowest low over N bars)
 * Upper = N-bar highest high (breakout above = bullish signal)
 * Lower = N-bar lowest low  (breakout below = bearish signal)
 * Middle = midpoint (dynamic support/resistance)
 */
export function calcDonchian(c, p) {
  const pr = p || 20;
  if (!c || c.length < pr) return { upper: [], middle: [], lower: [] };
  const u = [], mid = [], lo = [];
  for (let i = pr - 1; i < c.length; i++) {
    const sl = c.slice(i - pr + 1, i + 1);
    const hi = Math.max(...sl.map(d => d.h));
    const l  = Math.min(...sl.map(d => d.l));
    u.push({ time: c[i].t, value: hi });
    mid.push({ time: c[i].t, value: (hi + l) / 2 });
    lo.push({ time: c[i].t, value: l });
  }
  return { upper: u, middle: mid, lower: lo };
}

// ── Chart Type Transforms ─────────────────────────────────────────────────────

/**
 * calcHeikinAshi — Heikin Ashi candle transform
 * Smoothed candles that filter noise. Consecutive same-colour candles = strong trend.
 * haClose = avg(O,H,L,C); haOpen = avg(prev haO, prev haC)
 * Note: HA prices are synthetic — do NOT use for precise entry/exit price levels.
 */
export function calcHeikinAshi(c) {
  if (!c || !c.length) return [];
  const r = [];
  for (let i = 0; i < c.length; i++) {
    const d = c[i];
    const haC = (d.o + d.h + d.l + d.c) / 4;
    const haO = i === 0 ? (d.o + d.c) / 2 : (r[i - 1].o + r[i - 1].c) / 2;
    r.push({ ...d, o: haO, h: Math.max(d.h, haO, haC), l: Math.min(d.l, haO, haC), c: haC });
  }
  return r;
}

/**
 * calcRenko — Renko bricks
 * Each brick represents a fixed price move (brickSize). Time is ignored.
 * Renko filters out noise and shows pure price trends. No wicks.
 * brickSize is typically set to 1× ATR(14) for adaptive sizing.
 */
export function calcRenko(c, bs) {
  if (!c || !c.length || !bs || bs <= 0) return [];
  const br = [];
  let cur = Math.round(c[0].c / bs) * bs;
  for (let i = 1; i < c.length; i++) {
    const diff = c[i].c - cur;
    if (Math.abs(diff) >= bs) {
      const n = Math.floor(Math.abs(diff) / bs), dir = diff > 0 ? 1 : -1;
      for (let j = 0; j < n; j++) {
        const bO = cur + dir * j * bs, bC = bO + dir * bs;
        br.push({ t: c[i].t, o: dir > 0 ? bO : bC, h: Math.max(bO, bC), l: Math.min(bO, bC), c: dir > 0 ? bC : bO, v: c[i].v });
      }
      cur = cur + dir * n * bs;
    }
  }
  return br;
}

// ── Anchored Indicators ───────────────────────────────────────────────────────

/**
 * calcAnchoredVWAP — VWAP anchored to a specific timestamp
 * Unlike session VWAP (resets daily), anchored VWAP starts from a user-chosen point.
 * Used to measure average cost basis from earnings, IPO, or a key swing high/low.
 */
export function calcAnchoredVWAP(c, anchorTs) {
  if (!c || !c.length || !anchorTs) return [];
  const idx = c.findIndex(d => d.t >= anchorTs);
  if (idx < 0) return [];
  let ct = 0, cv = 0;
  return c.slice(idx).map(d => {
    const tp = (d.h + d.l + d.c) / 3;
    ct += tp * (d.v || 0);
    cv += d.v || 0;
    return { time: d.t, value: cv > 0 ? ct / cv : tp };
  });
}

/**
 * calcVolumeProfile — Price-level volume distribution
 * Divides the visible price range into N buckets and sums volume at each level.
 * Point of Control (POC) = price level with highest volume = strongest support/resistance.
 * Used by institutions to identify fair value zones.
 */
export function calcVolumeProfile(c, nb) {
  const buckets = nb || 24;
  if (!c || c.length < 2) return { levels: [], pocPrice: null, maxVol: 0 };
  const priceMin = Math.min(...c.map(d => d.l));
  const priceMax = Math.max(...c.map(d => d.h));
  const range = priceMax - priceMin;
  if (range <= 0) return { levels: [], pocPrice: null, maxVol: 0 };
  const bSize = range / buckets;
  const lvls = Array.from({ length: buckets }, (_, i) => ({
    price:       priceMin + (i + 0.5) * bSize,
    priceBottom: priceMin + i * bSize,
    priceTop:    priceMin + (i + 1) * bSize,
    volume: 0, upVol: 0, downVol: 0,
  }));
  c.forEach(d => {
    const tp  = (d.h + d.l + d.c) / 3;
    const idx = Math.min(Math.floor((tp - priceMin) / bSize), buckets - 1);
    if (idx >= 0) {
      lvls[idx].volume += d.v || 0;
      if (d.c >= d.o) lvls[idx].upVol += d.v || 0;
      else            lvls[idx].downVol += d.v || 0;
    }
  });
  const maxVol = Math.max(...lvls.map(b => b.volume));
  const poc    = lvls.reduce((best, b) => b.volume > best.volume ? b : best, lvls[0]);
  return { levels: lvls, pocPrice: poc.price, maxVol };
}

// ── Signal Detection ──────────────────────────────────────────────────────────

/**
 * detectSMACrossover — Golden cross / death cross detection
 * Compares last two values of two SMA series to detect crossovers.
 * Returns "golden_cross" (bullish), "death_cross" (bearish), or null.
 * Used for backend signal logging (logRef d3Signal).
 */
export function detectSMACrossover(s20, s50) {
  if (s20.length < 2 || s50.length < 2) return null;
  const [c20, p20, c50, p50] = [
    s20[s20.length - 1].value, s20[s20.length - 2].value,
    s50[s50.length - 1].value, s50[s50.length - 2].value,
  ];
  if (p20 <= p50 && c20 > c50) return "golden_cross";
  if (p20 >= p50 && c20 < c50) return "death_cross";
  return null;
}

// ── Market Status ─────────────────────────────────────────────────────────────

/**
 * getMarketStatus — Current US market session status
 * Returns: "open" | "pre" | "after" | "closed"
 * Uses browser's Intl API to get current ET time.
 * Used to show WebSocket live dot and extended hours context.
 */
export function getMarketStatus() {
  const et = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  const m  = et.getHours() * 60 + et.getMinutes();
  const d  = et.getDay();
  if (d === 0 || d === 6 || m < 240) return "closed";
  if (m < 570)  return "pre";
  if (m < 960)  return "open";
  if (m < 1200) return "after";
  return "closed";
}

// ── Display Helpers ───────────────────────────────────────────────────────────

/**
 * fmtPrice — Format a number as a USD price string
 * Handles null/undefined gracefully with "—" fallback.
 * Uses toLocaleString for thousands separators (e.g. $1,234.56)
 */
export function fmtPrice(v) {
  return v == null ? "—" : "$" + Number(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * fmtVol — Format a volume number with K/M/B suffix
 * e.g. 1234567 → "1.2M", 43200000 → "43.2M"
 */
export function fmtVol(v) {
  if (!v) return "—";
  if (v >= 1e9) return (v / 1e9).toFixed(2) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(0) + "K";
  return String(v);
}
